/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { convertCelsius } from '@/utils/conversions'

import { LANE_GAP_FRACTION } from './constants'

/** With no temperatures to plot, a typical reefer range: about -31°F to 59°F. */
const DEFAULT_RANGE_C: [number, number] = [-35, 15]

/** The temperature band is never narrower than this, so a flat line still has an axis around it. */
const MIN_SPAN_C = 3

export interface YAxisLayoutInput {
  /** The lowest and highest plotted temperature, or null when nothing is plotted. */
  data: [number, number] | null
  /** Threshold lines shown on the chart, which have to stay on the axis. */
  limits: number[]
  /** Status lanes (power, movement, zone mode, alarms) drawn below the temperatures. */
  laneCount: number
  /** CIT bars stacked above the temperatures. */
  citCount: number
  /** The status lanes live in a second grid, so this axis is just the temperatures. */
  belowChart: boolean
  units: 'Imperial' | 'Metric'
}

export interface YAxisLayout {
  /** The temperature band: tick labels outside it (over the lanes or CIT bars) are blanked. */
  tempMin: number
  tempMax: number
  /** `tempMax - tempMin`. Lane and bar sizes are fractions of it, so they look the same in either unit. */
  span: number
  axisMin: number
  axisMax: number
  /** Where status lane `index` sits, top to bottom. */
  laneY: (index: number) => number
  barHeight: number
  barGap: number
}

/**
 * The single-grid temperature chart's y-axis: the temperature band, with the status lanes
 * packed under it and the CIT bars over it. The band covers the data and the threshold lines,
 * so the lanes always sit at the bottom: a zone with no temperatures gets a default reefer
 * range, and a nearly flat one is widened to a minimum span, where otherwise rounding the axis
 * to whole degrees left the lanes floating mid-chart.
 */
export const yAxisLayout = ({
  data,
  limits,
  laneCount,
  citCount,
  belowChart,
  units,
}: YAxisLayoutInput): YAxisLayout => {
  const toUnits = (c: number) => convertCelsius(c, units)
  let [low, high] = data ?? [toUnits(DEFAULT_RANGE_C[0]), toUnits(DEFAULT_RANGE_C[1])]
  if (limits.length) {
    low = Math.min(low, ...limits)
    high = Math.max(high, ...limits)
  }
  const minSpan = toUnits(MIN_SPAN_C) - toUnits(0)
  if (high - low < minSpan) {
    const mid = (low + high) / 2
    low = mid - minSpan / 2
    high = mid + minSpan / 2
  }

  const tempMin = Math.floor(low)
  const tempMax = Math.ceil(high)
  const span = tempMax - tempMin
  const barHeight = span * 0.025
  const barGap = span * 0.005
  const laneGap = span * LANE_GAP_FRACTION
  const lanesBelow = !belowChart && laneCount > 0
  // Space at the top of the axis for the CIT bars, which are overlays only in the single grid.
  const citSpace = !belowChart && citCount > 0 ? citCount * (barHeight + barGap) + span * 0.02 : 0

  return {
    tempMin,
    tempMax,
    span,
    // Not rounded with lanes below: whole degrees could add up to a degree under them. The
    // labels down there are blanked, so an odd minimum never shows.
    axisMin: lanesBelow ? tempMin - laneGap * laneCount - span * 0.03 : Math.floor(tempMin - span * 0.05),
    axisMax: Math.ceil(tempMax + citSpace + span * 0.05),
    laneY: (index) => tempMin - laneGap * (index + 1),
    barHeight,
    barGap,
  }
}
